import { Document, Page, Text, View, StyleSheet, Image, Font, Line, Svg } from '@react-pdf/renderer'

const formatDateWithSuperscript = (dateString) => {
    if (!dateString) return '';

    const parts = dateString.split(' ');

    if (parts.length < 3) return dateString;

    let day = parts[0].match(/\d+/)[0];
    const suffix = parts[0].replace(/\d+/, '');
    const month = parts[1];
    const year = parts[2];

    day = day.padStart(2, '0');

    return { day, suffix, month, year };
};

const splitTextAndDate = (input = "") => {
    const text = normalizeInput(input);

    const dateRegex = /(\d{1,2})(st|nd|rd|th)\s+([A-Za-z]+)\s+(\d{4})/i;
    const match = text.match(dateRegex);

    if (!match) {
        return {
            prefixText: text,
            day: null,
            suffix: null,
            month: null,
            year: null,
        };
    }

    const [fullDate, day, suffix, month, year] = match;

    const prefixText = text.replace(fullDate, "").trim();

    return {
        prefixText,
        day,
        suffix,
        month,
        year,
    };
};

const normalizeInput = (text = "") => {
    return text
        .replace(/[\u0000-\u001F\u007F-\u009F]/g, "")
        .replace(/ᵗʰ/g, "th")
        .replace(/ˢᵗ/g, "st")
        .replace(/ⁿᵈ/g, "nd")
        .replace(/ʳᵈ/g, "rd")
        .trim();
};

const CDCCertificate = ({ data }) => {

    const formattedDate = formatDateWithSuperscript(data[0]?.issued_date);
    const formattedCertificateDate = formatDateWithSuperscript(data[0]?.issued_date);

    const { prefixText, day, suffix, month, year } =
        splitTextAndDate(data[0]?.completion_date);

    const templateTypeKey = `${data[0]?.certificate_template?.name}_${data[0]?.certificate_template?.certificate_type?.name}`
        .replace(/\s+/g, "_")
        .toUpperCase();

    const hasSecondSignature = data[0]?.signature_name_2 && data[0]?.signature_designation_2;

    Font.register({
        family: 'Open Sans',
        src: require('../../../../assets/fonts/OpenSans-Regular.ttf')
    });
    Font.register({
        family: 'Open Sans Semi Bold',
        src: require('../../../../assets/fonts/OpenSans-SemiBold.ttf')
    });
    Font.register({
        family: 'Open Sans Bold',
        src: require('../../../../assets/fonts/OpenSans-Bold.ttf')
    });
    Font.register({
        family: 'Open Sans Extra Bold',
        src: require('../../../../assets/fonts/OpenSans-ExtraBold.ttf')
    });
    Font.register({
        family: 'Open Sans Semi Bold Italic',
        src: require('../../../../assets/fonts/OpenSans-SemiBoldItalic.ttf')
    });
    Font.register({
        family: 'Open Sans Italic',
        src: require('../../../../assets/fonts/OpenSans-Italic.ttf')
    });

    // Styles
    const styles = StyleSheet.create({
        page: {
            fontFamily: 'Open Sans',
            position: 'relative',
            padding: 0, // CRITICAL: Must be 0 to remove white page margins
        },
        borderImage: {
            position: 'absolute',
            top: -4.5,
            left: -3,
            width: '101%',
            height: '101%',
            objectFit: 'cover',
        },
        contentWrapper: {
            paddingTop: 45,
            paddingBottom: 25,
            paddingHorizontal: 20,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between'
        },
        textTitle: {
            fontFamily: 'Open Sans Bold',
            fontSize: 28,
            textAlign: 'justify',
            marginBottom: 10,
        },
        text: {
            fontFamily: 'Open Sans Bold',
            fontSize: 18,
            textAlign: 'justify',
            fontWeight: 100,
            marginBottom: 30,
        },
        stuName: {
            fontFamily: 'Open Sans Italic',
            fontSize: 24,
            textAlign: 'justify',
            marginBottom: 30,
        },
        degreeText: {
            fontFamily: 'Open Sans Bold',
            fontSize: 18,
            textAlign: 'center',
            marginTop: 10,
            marginBottom: 10,
            marginLeft: 10,
            marginRight: 10
        },
        textSentence: {
            fontFamily: 'Open Sans',
            fontSize: 16,
            textAlign: 'center',
            fontWeight: 100,
            marginBottom: 0,
        },
        uniText: {
            fontFamily: 'Open Sans Bold',
            fontSize: 15,
            textAlign: 'justify',
            marginTop: 10,
            marginBottom: 10,
        },
        detailsText: {
            fontFamily: 'Open Sans',
            fontSize: 8,
            textAlign: 'justify',
            fontWeight: 600,
            marginBottom: 2,
        },
        signatureText: {
            fontFamily: 'Open Sans',
            fontSize: 15,
            textAlign: 'justify',
            fontWeight: 200,
            marginTop: 30,
            marginBottom: 20,
        },
        signatureTextTop: {
            fontFamily: 'Open Sans',
            fontSize: 11,
            textAlign: 'center',
            fontWeight: 200,
            marginTop: 5,
        },
        signatureTextTop2: {
            fontFamily: 'Open Sans',
            fontSize: 11,
            textAlign: 'center',
            fontWeight: 200,
            marginTop: 0,
        },
        signatureTextBottom: {
            fontFamily: 'Open Sans',
            fontSize: 11,
            textAlign: 'center',
            fontWeight: 200,
            marginBottom: 50,
        },
        scanText: {
            fontSize: 5,
            textAlign: 'justify',
            fontWeight: 100,
            marginBottom: 20,
        },
        superscript: {
            fontSize: 10,
            verticalAlign: 'super',
        },
        image: {
            width: 112
        },
        logoSec: {
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            marginBottom: 25,
        },
        approveSec: {
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "flex-end",
            marginTop: 20
        },
        leftSec: {
            flex: 1,
            alignItems: "center",
        },
        stampSec: {
            flex: 1,
            alignItems: "center",
        },
        stampImage: {
            marginTop: 25,
            width: 140,
        },
        rightSec: {
            flex: 1,
            alignItems: "center",
        },
        qrImage: {
            marginTop: 15,
            width: 70,
            height: 70,
        },
        bodySec: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
        },
    });

    return (
        <Document>
            <Page size="A4" style={styles.page}>
                <Image src={data[0]?.certificate_template?.background_image} style={styles.borderImage} fixed />
                <View style={styles.contentWrapper}>
                    <View style={styles.logoSec}>
                        <Image style={styles.image} src={data[0]?.certificate_template?.header_logo} />
                    </View>
                    <View style={styles.bodySec}>
                        <Text style={styles.textTitle} hyphenationCallback={(word) => [word]}>{data[0]?.certificate_phrase_1}</Text>
                        <Text style={styles.text} hyphenationCallback={(word) => [word]}>{data[0]?.certificate_phrase_2}</Text>
                        <Text style={styles.stuName} hyphenationCallback={(word) => [word]}>{data[0]?.student_name}</Text>
                        <Text style={styles.textSentence}
                            hyphenationCallback={(word) => [word]}>
                            {data[0]?.certificate_phrase_3}
                        </Text>
                        <Text style={styles.degreeText}
                            hyphenationCallback={(word) => [word]}>{data[0]?.other_course}</Text>
                        <Text style={styles.textSentence} hyphenationCallback={(word) => [word]}>{data[0]?.conducted_by_phrase}</Text>
                        <Text style={styles.uniText} hyphenationCallback={(word) => [word]}>{data[0]?.conducted_by}</Text>
                        <Text style={styles.textSentence} hyphenationCallback={(word) => [word]}>
                            {data[0]?.completion_phrase}
                            {prefixText} {day}
                            {suffix && (
                                <Text style={styles.superscript}>
                                    {suffix}
                                </Text>
                            )}{" "}
                            {month} {year}
                        </Text>
                    </View>
                    <View style={styles.approveSec}>
                        <View
                            style={[
                                styles.leftSec
                            ]}
                        >
                            {hasSecondSignature && (
                                <>
                                    <Text style={styles.signatureTextTop}
                                        hyphenationCallback={(word) => [word]}>
                                        {data[0]?.signature_name_2}
                                    </Text>
                                    <Text style={styles.signatureTextBottom}
                                        hyphenationCallback={(word) => [word]}>
                                        {data[0]?.signature_designation_2}
                                    </Text>
                                </>
                            )}
                            <Text style={styles.signatureTextTop}
                                hyphenationCallback={(word) => [word]}>
                                {data[0]?.signature_name_1}
                            </Text>
                            <Text style={styles.signatureTextTop2}
                                hyphenationCallback={(word) => [word]}>
                                {data[0]?.signature_designation_1}
                            </Text>
                        </View>

                        <View style={styles.stampSec}>
                            <Image style={styles.stampImage} src={data[0]?.certificate_template?.footor_logo} />
                        </View>
                        <View style={styles.rightSec}>
                            <Image style={styles.qrImage} src={data[1]} />
                            <Text style={styles.scanText}>Scan to verify certificate.</Text>
                            <Text style={styles.detailsText}>Document Ref.: {data[0]?.document_ref}</Text>
                            <Text style={styles.detailsText}>Issued Date: {formattedDate.day} {formattedDate.month} {formattedDate.year}</Text>
                        </View>
                    </View>
                </View>
            </Page>
        </Document>
    );
}

export default CDCCertificate;